import { useCallback, useMemo } from "react";

import { Button, Dialog, DialogActions, DialogSurface } from "@fluentui/react-components";
import { GiftOpenRegular, RocketRegular } from "@fluentui/react-icons";
import { CancelIcon } from "@fluentui/react-icons-mdl2";
import { Form, Formik } from "formik";
import { useAtomValue } from "jotai";
import { devices } from "library/constants";
import {
  EAttachmentType,
  EInsurerConsentType,
  EPaymentType,
  ETaxonomy,
  ICardBenefitPackage,
  IConsentDeliveryType,
} from "library/types";
import { formatDateFieldForPayload } from "library/utils";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { selectedClientAtom } from "store";
import styled from "styled-components";
import * as yup from "yup";

import { AppLogoSpinner } from "components/AppLogoSpinner/AppLogoSpinner";
import { PrimaryButton } from "components/PrimaryButton/PrimaryButton";
import { Flex } from "components/styled";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";
import { useEnrollPackage } from "state/useEnrollPackage";
import { useInvalidateEmployeeData } from "state/useGetEmployeeData";
import { useUpgradeEnrollment } from "state/useUpgradeEnrollment";

import { EnrollPackageFormItems } from "./EnrollPackageFormItems";

import { useIsSalaryDeductionConsentGivenNew } from "./hooks/useIsSalaryDeductionConsentGivenNew";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  benefitPackage: ICardBenefitPackage;
  isUpgradeEnroll?: boolean;
  isGrECoSensitiveDataConsentGiven?: boolean;
};

export function EnrollPackageDialog({
  open,
  setOpen,
  benefitPackage,
  isUpgradeEnroll,
  isGrECoSensitiveDataConsentGiven,
}: Props) {
  const { t } = useTranslation();
  const client = useAtomValue(selectedClientAtom);
  const invalidateEmployeeData = useInvalidateEmployeeData();
  // const isSalaryDeductionConsentGiven = useIsSalaryDeductionConsentGivenOld();
  const isSalaryDeductionConsentGiven = useIsSalaryDeductionConsentGivenNew(benefitPackage);

  const consentAttachmentType = useTaxonomyOptions(ETaxonomy.AttachmentType)?.find(
    (item) => item.code === EAttachmentType.EMPLOYEE_CONSENT
  )?.value;

  const salaryDeductionAttachmentType = useTaxonomyOptions(ETaxonomy.AttachmentType)?.find(
    (item) => item.code === EAttachmentType.SALARY_DEDUCTION
  )?.value;

  const salaryDeductionPaymentType = useTaxonomyOptions(ETaxonomy.PaymentType)?.find(
    (item) => item.code === EPaymentType.SALARY_DEDUCTION
  )?.value;

  const isSalaryDeductionPaymentType = salaryDeductionPaymentType === benefitPackage?.paymentType;

  const salaryDeductionTemplateOnClient = client?.salaryDeductionConsentDocumentPath;

  const { mutate: onEnrollPackage, isLoading: enrollInProgress } = useEnrollPackage(false);
  const { mutate: onUpgradePackage, isLoading: upgradeEnrollInProgress } =
    useUpgradeEnrollment(false);

  const consentOptions = useTaxonomyOptions(ETaxonomy.ConsentType);
  const consentDeliveryOptions = useTaxonomyOptions(ETaxonomy.DeliveryType);

  const insurerConsentType = consentOptions?.find(
    (consentType) => consentType.value === benefitPackage?.insurerConsentType
  );

  const showGrecoSensitiveDataConsentCheckbox =
    benefitPackage?.consentsNeeded && !isGrECoSensitiveDataConsentGiven;

  const showConsentTextSection = !!insurerConsentType && !isGrECoSensitiveDataConsentGiven;

  const handleEnrollPackage = useCallback(
    (values) => {
      const mutationFn = isUpgradeEnroll ? onUpgradePackage : onEnrollPackage;
      const consentDocuments = values["scannedSignedConsentDocument"] || [];
      const salaryDeductionConsentDoc = values["salaryDeductionSignedDoc"];

      mutationFn(
        {
          countryCode: Number(benefitPackage.countryCode),
          clientInternalNumberGOS: Number(benefitPackage.clientInternalNumberGos),
          benefitPackageTag: Number(benefitPackage.benefitPackageTag),
          employeeDependentTag: Number(benefitPackage.employeeDependentTag) || 0,
          employeeEnrollmentTag: Number(benefitPackage.employeeEnrollmentTag),
          dateOfEnrollment: formatDateFieldForPayload(values["dateOfEnrollment"]) || "",
          payload: {
            ...(showConsentTextSection
              ? {
                  consentText: values.consentText,
                  isGrECoSensitiveDataConsentGiven: values.isGrECoSensitiveDataConsentGiven,
                }
              : {}),
            attachments: [
              ...(consentDocuments?.map((document) => {
                return {
                  ...document,
                  attachmentType: consentAttachmentType,
                };
              }) || []),
              ...(salaryDeductionConsentDoc
                ? [
                    {
                      attachmentName: salaryDeductionConsentDoc?.name,
                      attachmentContent: salaryDeductionConsentDoc?.base64String || "",
                      attachmentMimeType: salaryDeductionConsentDoc?.type || "",
                      attachmentType: salaryDeductionAttachmentType,
                    },
                  ]
                : []),
            ],
          },
        },
        {
          onSuccess: () => {
            invalidateEmployeeData();
            toast.success(t("hbh.packageEnrolledSuccessfully.label"));
            setOpen(false);
          },
        }
      );
    },
    [benefitPackage, onEnrollPackage, isUpgradeEnroll]
  );

  const initialValues = useMemo(() => {
    if (!benefitPackage) return {};

    const initialData = {
      cancellationPossibility: benefitPackage?.cancellationPossibility,
      iAgreeTextConsent: false,
      dateOfEnrollment: new Date(),
      insurerConsentType,
      insurerConsentText: benefitPackage?.insurerConsentText,
      insurerConsentUrl: benefitPackage?.insurerConsentUrl || "",
      insurerConsentDeliveryType: consentDeliveryOptions?.find(
        (consentType) => consentType.value === benefitPackage?.insurerConsentDeliveryType
      ),
      insurerConsentDocumentPath: benefitPackage?.insurerConsentDocumentPath,
    };

    return initialData;
  }, [benefitPackage, consentDeliveryOptions, consentOptions]);

  if (!benefitPackage) {
    return <AppLogoSpinner />;
  }

  const isLoading = enrollInProgress || upgradeEnrollInProgress;

  const hasConsentDocument = benefitPackage.enrollmentDocuments?.some(
    (doc) => doc.attachmentType === consentAttachmentType
  );

  const validationSchema = yup.object().shape({
    dateOfEnrollment: yup.mixed().required(),

    scannedSignedConsentDocument: yup
      .mixed()
      .when(
        ["insurerConsentType", "insurerConsentDeliveryType"],
        ([insurerConsentType, insurerConsentDeliveryType]) => {
          const insurerFormConsentType =
            insurerConsentType?.value === EInsurerConsentType.INSURER_FORM;

          const isInsurerFormConsentDeliveryTypeUploadScan =
            insurerFormConsentType &&
            insurerConsentDeliveryType?.code === IConsentDeliveryType.UploadScan;
          if (
            insurerFormConsentType &&
            isInsurerFormConsentDeliveryTypeUploadScan &&
            !isUpgradeEnroll &&
            !hasConsentDocument
          ) {
            // return yup.array().min(1).required();
            return yup.mixed().nullable();
          }
          return yup.mixed().nullable();
        }
      ),
    iAgreeTextConsent: yup.mixed().when(["insurerConsentType"], ([insurerConsentType]) => {
      const iAgreeTextConsentType = insurerConsentType?.value === EInsurerConsentType.I_AGREE_TEXT;
      if (iAgreeTextConsentType && !isUpgradeEnroll && !hasConsentDocument) {
        return yup.boolean().oneOf([true]).required();
      }
      return yup.boolean().nullable();
    }),
    consentText: yup.mixed().when(["insurerConsentType"], ([insurerConsentType]) => {
      if (insurerConsentType && !isGrECoSensitiveDataConsentGiven) {
        return yup.string().min(2).required();
      }
      return yup.string().nullable();
    }),
  });

  return (
    <Dialog modalType="alert" open={open} onOpenChange={(event, data) => setOpen(data.open)}>
      <StyledDialogSurface
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {isLoading && <AppLogoSpinner />}
        <Formik
          validationSchema={validationSchema}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={(values, actions) => {
            // handleSubmit(values);
            handleEnrollPackage(values);
            actions.setSubmitting(false);
          }}
        >
          {({ errors }) => {
            const hasErrors = Object.keys(errors).length > 0;
            return (
              <Form>
                <EnrollPackageFormItems
                  hideConsentFields={hasConsentDocument || isUpgradeEnroll}
                  benefitPackage={benefitPackage}
                  // showConsentTextSection={showConsentTextSection}
                  showSalaryDeductionConsent={
                    isSalaryDeductionConsentGiven ? false : !!isSalaryDeductionPaymentType
                  }
                  isSalaryDeductionPaymentType={isSalaryDeductionPaymentType}
                  showGrecoSensitiveDataConsentCheckbox={showGrecoSensitiveDataConsentCheckbox}
                />
                <Flex width="100%" $justify="flex-end" $gap={"10px"}>
                  <Button
                    onClick={() => {
                      setOpen(false);
                    }}
                    appearance="secondary"
                    icon={
                      <CancelIcon
                        style={{
                          fontSize: 16,
                        }}
                      />
                    }
                  >
                    {t("greco.cancel")}
                  </Button>
                  <DialogActions>
                    <PrimaryButton
                      data-help="benefits-enroll-submit"
                      disabled={hasErrors}
                      icon={isUpgradeEnroll ? <RocketRegular /> : <GiftOpenRegular />}
                      style={{
                        paddingLeft: "25px",
                        paddingRight: "25px",
                      }}
                    >
                      {isUpgradeEnroll ? t("hbh.upgrade.label") : t("hbh.enroll.label")}
                    </PrimaryButton>
                  </DialogActions>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      </StyledDialogSurface>
    </Dialog>
  );
}

const StyledDialogSurface = styled(DialogSurface)`
  margin-left: 10px;
  margin-right: 10px;
  width: 95%;
  min-width: 95%;
  max-height: 98vh;
  padding: 15px;

  @media only screen and ${devices.md} {
    width: 600px;
    min-width: 600px;
    padding: 24px;

    margin-left: auto;
    margin-right: auto;
  }
`;
